import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { updateCustomerProfile } from "../../redux/actions/profileAction";
import { getCustomerProfileActionFn } from "../../redux/actions/userAction";
import Autocomplete from "@mui/material/Autocomplete";
import { profession } from "../../constant/filterMenuItems";
import {
  Grid,
  Button,
  TextField,
  Select,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Header from "../../components/Header";
import LoadingIndicator from "../../components/loadingIndicator";
import * as yup from "yup";
import { useGetProfessionQuery } from "../../state/api";

const validationSchema = yup.object().shape({
  fullname: yup.string().required("Full name is required"),
  // profilePicture: yup.mixed(),
  dob: yup
    .date()
    .nullable()
    .typeError("Date of birth is required")
    .required("Date of birth is required"),
  addressLine: yup.string().required("Address-Line is required"),
  postCode: yup.string().required("Zip-code is required"),
});
const EditUser = () => {
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const userId = pathname.split("/").pop();
  const [loading, setLoading] = useState(false);
  const { data: professionResponse = {} } = useGetProfessionQuery();
  const professionData = professionResponse.professions || [];
  const [selectedProfessions, setSelectedProfessions] = useState([]);

  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    username: "",
    gender: "",
    // userType: "",
    profession: "",
    professionId: "",
    dob: "",
    age: "",
    mobileNumber: "",
    addressLine: "",
    postCode: "",
    fbLink: "",
    linkedinLink: "",
    tiktokLink: "",
    availableLocation: "",
    availableTime: "",
    preferredDuration: "",
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await dispatch(getCustomerProfileActionFn(userId));
        if (response.response && response.success) {
          const userDetails = response.response[0];
          userDetails.dob = userDetails.dob ? userDetails.dob.slice(0, 10) : "";
          const normalizedGender = userDetails.gender
            ? userDetails.gender.charAt(0).toUpperCase() +
              userDetails.gender.slice(1).toLowerCase()
            : "";
          setFormData((prevState) => {
            const merged = { ...prevState, ...userDetails, gender: normalizedGender };
            // Keep every field a defined string/number so inputs stay controlled
            // even when the API omits or nulls out a field.
            Object.keys(merged).forEach((key) => {
              if (merged[key] === null || merged[key] === undefined) {
                merged[key] = "";
              }
            });
            return merged;
          });

          // Map the user's profession title to the selectedProfessions
          const userProfessions = (userDetails.profession || "")
            .split(",")
            .map((prof) => prof.trim())
            .filter(Boolean);
          const selected = professionData.filter((profession) =>
            userProfessions.includes(profession.title)
          );
          setSelectedProfessions(selected);
        } else {
          console.log("Failed to fetch user's details");
          toast.error("Failed to fetch user's details");
        }
      } catch (error) {
        console.log("Error fetching user details", error);
        toast.error("Error fetching user details");
      }
    };

    fetchUserDetails();
  }, [userId, dispatch, professionData]);

  const navigate = useNavigate();
  const [isEditable, setIsEditable] = useState(false);
  const [err, setErr] = useState({});
  const [isDbProfilePic, setIsDbProfilePic] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleProfessionChange = (event, newValue) => {
    setSelectedProfessions(newValue);

    const professionTitles = newValue.map((profession) => profession.title);

    setFormData((prevState) => ({
      ...prevState,
      profession: professionTitles,
    }));
  };

  const handleImageChange = (e) => {
    setIsDbProfilePic(false);
    setFormData((prevformData) => ({
      ...prevformData,
      profilePicture: e.target.files[0],
    }));
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      // Clear error message if validation passes
      setErr((prevErr) => ({
        ...prevErr,
        [fieldName]: "",
      }));
    } catch (error) {
      // Set error message if validation fails
      setErr((prevErr) => ({
        ...prevErr,
        [fieldName]: error.message,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // Start loading
      // Validate form data
      await validationSchema.validate(formData, { abortEarly: false });

      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      selectedProfessions.forEach((profession) => {
        formDataToSend.append("professions[]", profession.title);
      });
      const isSuccess = await dispatch(
        updateCustomerProfile(userId, formDataToSend)
      );
      // Display the key/value pairs
      // for (var pair of formDataToSend.entries()) {
      //   console.log("formAppend", pair[0] + ", " + pair[1]);
      // }
      if (isSuccess) {
        navigate("/user");
        toast.success("Customer updated successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.warning("Customer Update Failed!");
      }
    } catch (validationErrors) {
      console.error("Validation error occurred:", validationErrors);
      // Handle validation errors
      const errors = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach((validationError) => {
          errors[validationError.path] = validationError.message;
        });
      }
      setErr(errors);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="EDIT USER" />
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* USER NAME */}
          <Grid item sm={12} md={4}>
            <TextField
              label="User Name"
              name="username"
              value={formData.username}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            {!isEditable && (
              <Typography variant="caption" style={{ color: "green" }}>
                This field is not editable
              </Typography>
            )}
          </Grid>
          {/* EMAIL */}
          <Grid item sm={12} md={4}>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            {!isEditable && (
              <Typography variant="caption" style={{ color: "green" }}>
                This field is not editable
              </Typography>
            )}
          </Grid>
          {/* CONTACT NUMBER */}
          <Grid item sm={12} md={4}>
            <TextField
              label="Mobile Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            {!isEditable && (
              <Typography variant="caption" style={{ color: "green" }}>
                This field is not editable
              </Typography>
            )}
          </Grid>
          {/* FIRST NAME */}
          <Grid item sm={12} md={4}>
            <TextField
              label="Full Name"
              name="fullname"
              value={formData.fullname}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            />
            {err.fullname && (
              <Grid item>
                <Typography color="error">{err.fullname}</Typography>
              </Grid>
            )}{" "}
          </Grid>

          {/* PROFESSION */}
          <Grid item sm={12} md={8}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="profession"
                options={professionData}
                getOptionLabel={(option) => option.title}
                closeOnSelect={false}
                value={selectedProfessions}
                onChange={handleProfessionChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Profession"
                    placeholder="Select Profession"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* USER TYPE */}
          {/* <Grid item sm={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <MenuItem value="Regular">Regular</MenuItem>
                <MenuItem value="Celebrity">Celebrity</MenuItem>
              </Select>
            </FormControl>
            {err.gender && (
              <Grid item>
                <Typography color="error">{err.gender}</Typography>
              </Grid>
            )}{" "}
          </Grid> */}

          {/* GENDER */}
          <Grid item sm={12} md={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                margin="normal"
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* ADDRESS-LINE */}
          <Grid item sm={12} md={4}>
            <TextField
              label="Address-Line"
              name="addressLine"
              value={formData.addressLine}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
            {err.addressLine && (
              <Grid item>
                <Typography color="error">{err.addressLine}</Typography>
              </Grid>
            )}{" "}
          </Grid>
          {/* POSTAL CODE */}
          <Grid item sm={12} md={4}>
            <TextField
              label="Postal/Zip-Code"
              name="postCode"
              value={formData.postCode}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
            {err.postCode && (
              <Grid item>
                <Typography color="error">{err.postCode}</Typography>
              </Grid>
            )}{" "}
          </Grid>
          {/* DATE OF BIRTH */}
          <Grid container item sm={12} md={6} alignItems="center">
            <Grid item>
              <span style={{ fontSize: "15px" }}>Date of Birth</span>
            </Grid>
            <Grid item sm={12} md={10} ml={1}>
              <TextField
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
              />
              {err.dob && (
                <Grid item>
                  <Typography color="error">{err.dob}</Typography>
                </Grid>
              )}{" "}
            </Grid>
          </Grid>
          {/* UPLOAD PROFILE PICTURE */}
          <Grid item sm={12} md={6}>
            <label
              htmlFor="profilePicture"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Button
                variant="outlined"
                color="info"
                component="span"
                size="large"
                style={{
                  width: "100%",
                  fontSize: "1rem",
                  textTransform: "none",
                }}
              >
                Change Profile Picture Image
              </Button>
              {isDbProfilePic ? (
                <img
                  src={"http://localhost:8000/" + formData.profilePicture}
                  // src={URL.createObjectURL(formData.profilePicture)}
                  alt="Profile Picture Image"
                  style={{
                    maxWidth: "100px",
                    maxHeight: "250px",
                    marginLeft: "1rem",
                  }}
                />
              ) : (
                <img
                  src={URL.createObjectURL(formData.profilePicture)}
                  alt="Profile Picture Image"
                  style={{
                    maxWidth: "100px",
                    maxHeight: "250px",
                    marginLeft: "1rem",
                  }}
                />
              )}
              <Input
                id="profilePicture"
                name="profilePicture"
                type="file"
                onChange={handleImageChange}
                onBlur={handleBlur}
                inputProps={{ accept: "image/*" }}
                style={{ display: "none" }}
              />{" "}
              {/* {err.profilePicture && (
                <Grid item>
                  <Typography color="error">{err.profilePicture}</Typography>
                </Grid>
              )}{" "} */}
            </label>
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Facebook Link"
              name="fbLink"
              value={formData.fbLink}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Linkedin Link"
              name="linkedinLink"
              value={formData.linkedinLink}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Tiktok Link"
              name="tiktokLink"
              value={formData.tiktokLink}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Available Location"
              name="availableLocation"
              value={formData.availableLocation}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Available Time"
              name="availableTime"
              value={formData.availableTime}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              label="Preferred Duration"
              name="preferredDuration"
              value={formData.preferredDuration}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
            />
          </Grid>

          <Grid container justifyContent="center" alignItems="center">
            <Button
              onSubmit={handleSubmit}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={loading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
      {loading && <LoadingIndicator />}
    </Box>
  );
};
export default EditUser;
